import {WorkspacePerson} from "@/types/Person";

/**
 * Um valor escolhível, com nome e cor: a opção de um domínio, a situação de um fluxo, o item
 * apontado por um relacionamento, a pessoa de um atributo de usuário.
 *
 * É o contrato que já unificou o campo de seleção e o campo de situação — dois componentes
 * quase iguais que só existiam separados porque a cor vinha de lugares diferentes. Com ele,
 * quem desenha recebe "id, rótulo e cor" e não precisa saber de onde cada coisa veio.
 *
 * Mora em `types` e não dentro do componente que o usa porque quem precisa dele agora inclui
 * utilitários puros — e utilitário não pode depender de componente. É a mesma inversão de
 * camada que já incomodou quando o `types/Workflow.ts` importava o StateColorizer de dentro
 * de `components/`.
 *
 * A cor é OPCIONAL, e o que muda é de ONDE ela vem: a opção de um domínio e a situação de um
 * fluxo trazem a cor que alguém escolheu; um item de coleção tem a sua derivada do id, pelo
 * `instanceColor`. Fica vazia só no que ainda não tem id — um item sendo criado.
 */
export type ColoredOption = {
    id: number
    name: string
    color?: string
    /** Só no valor que é uma pessoa: quem ela é, para a forma `person` desenhar a cara e o nome. */
    person?: WorkspacePerson
}

/**
 * O valor visto como uma raia do Kanban.
 *
 * O `final` é o que liga o prazo dos terminados, e só a situação de um fluxo o produz —
 * `coloredStates()` já devolve exatamente esta forma. Seleção e relacionamento simplesmente
 * não o trazem, e por isso a faixa deles não ganha o menu de prazo sem precisar de nenhuma
 * pergunta sobre "que tipo de dimensão é esta".
 */
export type LaneOption = ColoredOption & {
    final?: boolean
}

/**
 * Como um valor colorido se desenha. **São três, e a forma diz a NATUREZA do valor:**
 *
 * - `accent` — a régua à esquerda do nome: **item de coleção**. A cor vem do id, e o nome fica
 *   na cor de leitura, porque a cor aqui é mancha e não papel de texto.
 * - `dot` — a bolinha cheia antes do nome: **situação de um fluxo**. Lista curta, ordem
 *   desenhada, cor escolhida por alguém.
 * - `chip` — o nome dentro de uma caixa no tom: **opção de um domínio**.
 * - `person` — a cara, o nome e o e-mail (`PersonLine`): **pessoa do ambiente**, como ela aparece
 *   em todo o app.
 *
 * Mora no tipo, e não em cada tela, porque a escolha entre elas é sempre a mesma pergunta —
 * "que natureza tem este valor?" — e ela já estava sendo respondida à mão em cada lugar que
 * desenha valor de dimensão.
 */
export type ValueForm = 'accent' | 'dot' | 'chip' | 'person'

/**
 * As duas formas do ponto colorido, e o que cada uma quer dizer.
 *
 * **A bolinha cheia é valor de escolha** — opção de domínio, situação de fluxo: coisas de uma
 * lista curta, cuja cor alguém escolheu. Um item de coleção não usa bolinha nenhuma: ele se
 * marca com a RÉGUA do `ColorAccent`, em qualquer escala — ao lado do nome, na linha da
 * listagem, no cabeçalho da ficha. A forma diz a natureza, e não só a cor.
 *
 * O nome do ícone mora aqui, ao lado do contrato do valor colorido, porque ele aparece em meia
 * dúzia de arranjos diferentes — lista de um select, chip, célula de tabela —, cada um com o seu
 * tamanho e o seu respiro. O que se compartilha é a ESCOLHA do ícone; o arranjo é de cada tela.
 */
export const VALUE_DOT = 'mdi-circle-slice-8'
